import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BACKTEST_FILE_SUFFIX,
  IMPORT_CONFLICT_MODES,
  STRATEGY_FILE_SUFFIX,
  STRAT_READABLE_SUFFIXES,
  describeContents,
  providerLabel,
} from "@strat/format";
import type { ImportConflictMode, ImportReport, PortableContents, RunDataSource } from "@strat/format";
import { Card, Field } from "../components/ui";
import { dataService } from "../services/api";
import type { InventoryRow } from "../services/api";
import { readPortableFile } from "./file";

/** What the file picker offers. `.gz` alone because pickers match the last suffix. */
const PICKER_ACCEPT = [...STRAT_READABLE_SUFFIXES, ".gz", "application/json", "application/gzip"].join(",");

const CONFLICT_HELP: Record<ImportConflictMode, string> = {
  copy: "Keep both — the imported one is saved under a new id.",
  replace: "Overwrite the strategy already saved under that id.",
  skip: "Leave the existing strategy alone and don't import this one.",
};

/**
 * Reads a portable file, shows what's in it, and writes it in on confirmation.
 *
 * The preview is the point. An import that writes first and reports afterwards
 * makes the user find out what they agreed to by looking at their library —
 * so the file is parsed in the browser, its contents named, and nothing is sent
 * until someone has read that and pressed the button.
 */
export function ImportPanel({
  onImported,
  onClose,
  initialFile,
}: {
  onImported?: (report: ImportReport) => void;
  onClose?: () => void;
  /** A file that was dropped on the app or opened from the desktop: previewed at once. */
  initialFile?: File | null;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [filename, setFilename] = useState<string | null>(null);
  const [document, setDocument] = useState<unknown>(null);
  const [contents, setContents] = useState<PortableContents | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);

  const [withStrategies, setWithStrategies] = useState(true);
  const [withRuns, setWithRuns] = useState(true);
  const [onConflict, setOnConflict] = useState<ImportConflictMode>("copy");

  const reset = useCallback(() => {
    setFilename(null);
    setDocument(null);
    setContents(null);
    setErrors([]);
    setReport(null);
  }, []);

  const accept = useCallback(
    async (file: File) => {
      reset();
      setFilename(file.name);

      // Validated with the same function the server uses, so what is previewed
      // here is exactly what will be accepted there. A .gz is unpacked here, and
      // the server receives the plain document.
      const { value, read } = await readPortableFile(file);
      if (!read.ok) return setErrors(read.errors);

      setDocument(value);
      setContents(read.contents);
      setWithStrategies(read.contents.strategies.length > 0);
      setWithRuns(read.contents.runs.length > 0);
    },
    [reset],
  );

  useEffect(() => {
    if (initialFile) void accept(initialFile);
  }, [initialFile, accept]);

  async function apply() {
    if (!contents) return;
    setBusy(true);
    setErrors([]);
    try {
      const result = await dataService.importDocument(document, {
        strategies: withStrategies,
        runs: withRuns,
        onConflict,
      });
      setReport(result);
      queryClient.invalidateQueries({ queryKey: ["strategies"] });
      queryClient.invalidateQueries({ queryKey: ["strategy-facets"] });
      queryClient.invalidateQueries({ queryKey: ["runs"] });
      onImported?.(result);
    } catch (err) {
      setErrors([err instanceof Error ? err.message : String(err)]);
    } finally {
      setBusy(false);
    }
  }

  const hasBoth = !!contents && contents.strategies.length > 0 && contents.runs.length > 0;
  const nothingSelected = !!contents && !withStrategies && !withRuns;

  return (
    <Card
      title="Import"
      actions={
        onClose && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      <div
        className={`dropzone${dragging ? " dropzone--over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) void accept(file);
        }}
      >
        <span className="dropzone__icon" aria-hidden="true">
          ↓
        </span>
        <p className="dropzone__lead">
          Drop a <code>{STRATEGY_FILE_SUFFIX}</code> or <code>{BACKTEST_FILE_SUFFIX}</code> file here
        </p>
        <p className="muted">
          Strategies, backtests (also <code>.backtest.gz</code>), or a bundle of both. Older{" "}
          <code>.strat</code> files open too.
        </p>
        <button type="button" className="btn btn--sm" onClick={() => inputRef.current?.click()}>
          Choose file…
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={PICKER_ACCEPT}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void accept(file);
            // Cleared so picking the same file twice in a row still fires.
            e.target.value = "";
          }}
        />
      </div>

      {errors.length > 0 && (
        <div className="alert alert--error">
          <strong>{filename ? `Can't read ${filename}` : "Can't read that file"}</strong>
          <ul className="import__errors">
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {contents && !report && (
        <div className="import__preview">
          <div className="import__summary">
            <div>
              <strong>{filename}</strong>
              <p className="muted">
                Holds {describeContents(contents)}
                {contents.exportedAt ? `, exported ${contents.exportedAt.slice(0, 10)}` : ""}
                {contents.generator ? ` by ${contents.generator.name} ${contents.generator.version}` : ""}.
              </p>
            </div>
            <span className="badge badge--accent">{contents.kind}</span>
          </div>

          {contents.notes.map((note) => (
            <p key={note} className="import__note">
              {note}
            </p>
          ))}

          <ul className="import__list">
            {contents.strategies.map((entry, i) => (
              <li key={`s${i}`}>
                <span className="import__type">strategy</span>
                {entry.strategy.name}
                {entry.settings && (
                  <span className="muted">
                    {" "}
                    · suggested on {entry.settings.symbol} {entry.settings.timeframe}
                  </span>
                )}
              </li>
            ))}
            {contents.runs.map((entry, i) => (
              <li key={`r${i}`}>
                <span className="import__type">run</span>
                {entry.strategyName}
                <span className="muted">
                  {" "}
                  · {entry.symbol} {entry.timeframe} · {entry.result.stats.totalTrades}{" "}
                  {entry.result.stats.totalTrades === 1 ? "trade" : "trades"} ·{" "}
                  {entry.result.stats.totalReturnPct.toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>

          {contents.runs.length > 0 && <DataSources runs={contents.runs.map((r) => r.dataSource)} />}

          <div className="import__options">
            {hasBoth && (
              <>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={withStrategies}
                    onChange={(e) => setWithStrategies(e.target.checked)}
                  />
                  Strategies ({contents.strategies.length})
                </label>
                <label className="checkbox">
                  <input type="checkbox" checked={withRuns} onChange={(e) => setWithRuns(e.target.checked)} />
                  Runs ({contents.runs.length})
                </label>
              </>
            )}
            {withStrategies && contents.strategies.length > 0 && (
              <Field label="If a strategy already exists">
                <select
                  value={onConflict}
                  onChange={(e) => setOnConflict(e.target.value as ImportConflictMode)}
                >
                  {IMPORT_CONFLICT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode === "copy" ? "Import as a copy" : mode === "replace" ? "Replace mine" : "Skip it"}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <p className="muted" style={{ fontSize: "0.82rem" }}>
            {withStrategies && contents.strategies.length > 0 ? CONFLICT_HELP[onConflict] : ""}
            {contents.runs.length > 0 && withRuns
              ? " Runs are always added — a result is a record of something that happened, so importing never overwrites one."
              : ""}
          </p>

          <div className="row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || nothingSelected}
              onClick={() => void apply()}
            >
              {busy
                ? "Importing…"
                : `Import ${describeContents({
                    strategies: withStrategies ? contents.strategies : [],
                    runs: withRuns ? contents.runs : [],
                  })}`}
            </button>
            <button type="button" className="btn btn--ghost" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {report && <ImportReportView report={report} onAnother={reset} />}
    </Card>
  );
}

const sourceKeyOf = (s: Pick<RunDataSource, "symbol" | "timeframe" | "marketType">) =>
  `${s.symbol}|${s.timeframe}|${s.marketType}`;

const day = (ms: number | null) => (ms === null ? "the start" : new Date(ms).toISOString().slice(0, 10));

/** Whether this machine holds the bars a run was tested on — or how much of them. */
function coverage(source: RunDataSource, row: InventoryRow | undefined): "full" | "partial" | "none" {
  if (!row || row.candleCount === 0) return "none";
  const from = source.from ?? row.minTime;
  const to = source.to ?? row.maxTime;
  return row.minTime <= from && row.maxTime >= to ? "full" : "partial";
}

/**
 * Where each run's bars came from, and whether they are here.
 *
 * A file carries no candles — they are public and re-fetchable, and naming the
 * provider is what makes "the same series" mean the same prices. The import
 * itself never needs them: the stored result is complete. They matter for the
 * chart and for re-running, so missing ones are offered for download where
 * this app can download (the desktop app and the local lab).
 */
function DataSources({ runs }: { runs: (RunDataSource | undefined)[] }) {
  const sources = [
    ...new Map(
      runs.filter((r): r is RunDataSource => !!r).map((s) => [`${sourceKeyOf(s)}|${s.provider}`, s]),
    ).values(),
  ];
  const inventory = useQuery({ queryKey: ["inventory"], queryFn: () => dataService.getInventory() });
  const [state, setState] = useState<Record<string, string>>({});

  async function fetchBars(source: RunDataSource) {
    const key = sourceKeyOf(source);
    setState((s) => ({ ...s, [key]: "Starting download…" }));
    try {
      // Warm-up bars sit before `from`; a day of slack per hundred bars is
      // generous on any timeframe this app downloads for.
      const to = source.to ?? Date.now();
      const from =
        (source.from ?? to - 365 * 86_400_000) - Math.ceil(source.warmupBars / 100 + 1) * 86_400_000;
      await dataService.startDownload(source.symbol, source.timeframe, source.marketType, from, to);
      setState((s) => ({ ...s, [key]: "Downloading — follow it under Data." }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((s) => ({
        ...s,
        [key]: /hosted|403/i.test(message)
          ? "This dataset isn't hosted here. The result imports complete; only its chart needs the bars."
          : `Couldn't start the download — ${message}`,
      }));
    }
  }

  if (sources.length === 0) return null;
  return (
    <div className="import__sources">
      <p className="muted small">
        Market data is not stored in the file — each backtest names where its bars came from, and the chart
        fetches them here.
      </p>
      <ul className="import__list">
        {sources.map((source) => {
          const key = sourceKeyOf(source);
          const row = inventory.data?.find((r) => sourceKeyOf(r) === key);
          const have = inventory.data ? coverage(source, row) : null;
          return (
            <li key={`${key}|${source.provider}`}>
              <span className="import__type">data</span>
              {providerLabel(source.provider)} · {source.symbol} {source.marketType} {source.timeframe}
              <span className="muted">
                {" "}
                · {day(source.from)} → {day(source.to)}
                {have === "full" && " · available here"}
                {have === "partial" && " · partly available here"}
                {have === "none" && " · not available here"}
              </span>
              {have !== null && have !== "full" && !state[key] && (
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => void fetchBars(source)}
                >
                  Download data
                </button>
              )}
              {state[key] && <span className="muted small"> {state[key]}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ImportReportView({ report, onAnother }: { report: ImportReport; onAnother: () => void }) {
  const imported = report.strategies.imported.length + report.runs.imported.length;
  const skipped = report.strategies.skipped.length + report.runs.skipped.length;

  return (
    <div className="import__preview">
      <div className={imported > 0 ? "alert alert--ok" : "alert"}>
        <strong>
          Imported {imported} {imported === 1 ? "item" : "items"}
          {skipped > 0 ? `, skipped ${skipped}` : ""}.
        </strong>
      </div>

      <ul className="import__list">
        {report.strategies.imported.map((entry) => (
          <li key={entry.id}>
            <span className="import__type">strategy</span>
            {entry.name}
            <span className="muted"> · {entry.action}</span>
          </li>
        ))}
        {report.runs.imported.map((entry) => (
          <li key={entry.id}>
            <span className="import__type">run</span>
            {entry.label}
          </li>
        ))}
        {[...report.strategies.skipped, ...report.runs.skipped].map((entry) => (
          <li key={`${entry.label}-${entry.reason}`} className="import__skipped">
            <span className="import__type">skipped</span>
            {entry.label}
            <span className="muted"> · {entry.reason}</span>
          </li>
        ))}
      </ul>

      {report.notes.map((note) => (
        <p key={note} className="import__note">
          {note}
        </p>
      ))}

      <button type="button" className="btn btn--sm" onClick={onAnother}>
        Import another file
      </button>
    </div>
  );
}
